import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import MainCard from "../../../../../components/MainCard";

import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import _debounce from "lodash/debounce";

import { ColumnDef } from "@tanstack/react-table";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableSimpleTable from "../../../../../components/ReusableComponent/ReusableSimpleTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { ResumeCourse } from "../../../../../types/resumes-managment/resume";
import { EyeOutlined, CloseOutlined } from "@ant-design/icons";
import ExpandingResumeCourseDetail from "./ExpandingResumeCourseDetail";
import AddEditResumeCourseForm from "./AddEditResumeCourseForm";

const ResumeCourses = ({ resumeId }: { resumeId?: number }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );
  const [selected, setSelected] = useState<ResumeCourse | null>(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResumeCourse[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<ResumeCourse>[]>(
    () => [
      // {
      //   header: () => <FormattedMessage id="ID" />,
      //   accessorKey: "id",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">{row?.original.id}</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },
      {
        header: () => <FormattedMessage id="Institute" />,
        accessorKey: "institute",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.institute}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Course Name" />,
        accessorKey: "course_name",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.course_name}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Certificate Date" />,
        accessorKey: "certificate_date",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.certificate_date}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      // {
      //   header: () => <FormattedMessage id="Course Field" />,
      //   accessorKey: "course_field",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">
      //           {row?.original.course_field}
      //         </Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },
      // {
      //   header: () => <FormattedMessage id="Course Descriptions" />,
      //   accessorKey: "course_descriptions",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">
      //           {row?.original.course_descriptions}
      //         </Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row">
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/resume/courses/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Resume Course deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete resume course",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(`/resume/courses`);
      const { data, meta } = response.data;
      setResult(data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching articles",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MainCard
          title="Resume Courses"
          headerActions={
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setCustomerModal(true);
                setSelected(null);
              }}
            >
              Add Course
            </Button>
          }
        >
          <ReusableSimpleTable
            data={result}
            columns={columns}
            renderDetailPanel={({ row }: any) => (
              <ExpandingResumeCourseDetail data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </MainCard>
      )}
      <ReusableDeleteDialog
        title={selected?.course_name}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      {customerModal && (
        <AddEditResumeCourseForm
          setResult={setResult}
          item={selected}
          open={customerModal}
          setOpen={setCustomerModal}
          resumeId={resumeId}
        />
      )}
    </>
  );
};

export default ResumeCourses;
